/**
 * Username rules.
 *
 * The username is the account identifier, so these rules are close to
 * impossible to change once people hold names under them. Two decisions are
 * baked in here.
 *
 * **Non-Latin usernames are allowed.** The alternative privileges Latin-script
 * markets, and this is a worldwide product. The previous rule permitted Latin
 * and Hebrew, which is not the safe option -- it is the same risk surface as
 * allowing everything, restricted to two markets.
 *
 * **Mixed scripts are not allowed**, which is what makes the first decision
 * safe. Impersonation is the main attack on username systems: Cyrillic `а`
 * (U+0430) and Latin `a` (U+0061) are different characters that render
 * identically, so `dаna` and `dana` look like one name and are two accounts.
 * Requiring a single script per name means a Cyrillic homoglyph cannot be
 * smuggled into an otherwise Latin name -- the mixture is rejected outright,
 * without needing to ship and maintain the full TR39 confusables table.
 *
 * This is Unicode TR39's "Moderately Restrictive" level, approximated with the
 * script properties the JS regex engine already knows, plus the three
 * whole-script combinations that real languages need (Japanese, Chinese and
 * Korean all legitimately mix Han with another script).
 */

/** NFKC, then case-folded. This is the form uniqueness is checked against. */
export function canonicalUsername(input: string): string {
  // NFKC first: it collapses compatibility variants, so the fullwidth `ｄａｎａ`
  // and the ASCII `dana` cannot both be claimed. Lowercasing afterwards matters
  // -- doing it first would miss variants that only fold under NFKC.
  return input.normalize('NFKC').toLowerCase();
}

export type UsernameProblem =
  | 'too_short'
  | 'too_long'
  | 'bad_start'
  | 'disallowed_character'
  | 'mixed_script'
  | 'reserved';

const MIN_LENGTH = 3;
const MAX_LENGTH = 32;

/**
 * Names a person cannot self-assign.
 *
 * Two jobs. The `vav`-prefixed and role names stop someone registering
 * `support` and collecting other people's credentials -- the cheapest attack
 * there is, and one that costs us our own name to defend against afterwards.
 * The rest reserves the verified-business namespace before businesses exist,
 * because a handle squatted on day one has to be litigated away on day five
 * hundred.
 *
 * Checked against the canonical form, so `Ｓupport` and `sUpPoRt` are caught
 * by the same entry.
 */
const RESERVED_EXACT = new Set([
  'admin', 'administrator', 'root', 'system', 'support', 'help', 'helpdesk',
  'security', 'abuse', 'billing', 'payments', 'moderator', 'mod', 'staff',
  'official', 'verified', 'business', 'api', 'bot', 'noreply', 'no-reply',
  'postmaster', 'webmaster', 'info', 'contact', 'legal', 'privacy', 'status',
  'me', 'you', 'all', 'everyone', 'here', 'null', 'undefined', 'anonymous',
]);

/** Anything starting with these belongs to us or to the business namespace. */
const RESERVED_PREFIXES = ['vav', 'biz.', 'business.', 'official.'];

/**
 * Scripts that may appear together in one name.
 *
 * Common and Inherited carry digits, `_`, `.`, `-` and combining marks, so they
 * are always permitted alongside a real script.
 */
const ALLOWED_COMBINATIONS: readonly (readonly string[])[] = [
  ['Latin', 'Han', 'Hiragana', 'Katakana'], // Japanese
  ['Latin', 'Han', 'Bopomofo'], // Chinese
  ['Latin', 'Han', 'Hangul'], // Korean
];

/**
 * Scripts we can name in a regex and therefore enforce. A character whose
 * script is not in this list is rejected rather than waved through -- an
 * unrecognised script would otherwise bypass the mixing rule entirely.
 */
const SCRIPTS = [
  'Latin', 'Greek', 'Cyrillic', 'Armenian', 'Hebrew', 'Arabic', 'Syriac', 'Thaana',
  'Devanagari', 'Bengali', 'Gurmukhi', 'Gujarati', 'Oriya', 'Tamil', 'Telugu',
  'Kannada', 'Malayalam', 'Sinhala', 'Thai', 'Lao', 'Tibetan', 'Myanmar',
  'Georgian', 'Hangul', 'Ethiopic', 'Cherokee', 'Khmer', 'Mongolian', 'Han',
  'Hiragana', 'Katakana', 'Adlam', 'Tifinagh', 'Nko',
] as const;

const SCRIPT_TESTS = SCRIPTS.map((name) => ({ name, re: new RegExp(`\\p{Script=${name}}`, 'u') }));

/** Digits, the three punctuation marks we allow, and combining marks. */
const NEUTRAL = /[\p{Nd}\p{Mn}\p{Mc}_.-]/u;

/**
 * Validate a username. Returns undefined when it is acceptable.
 *
 * Length is counted in code points, not UTF-16 units, so an emoji-free name in
 * Devanagari is not silently shorter than the same name in Latin.
 */
export function validateUsername(input: string): UsernameProblem | undefined {
  const canonical = canonicalUsername(input.trim());
  const chars = [...canonical];

  if (chars.length < MIN_LENGTH) return 'too_short';
  if (chars.length > MAX_LENGTH) return 'too_long';

  // A name may not start with punctuation: leading dots and dashes are how you
  // build a name that reads as a different one in a list.
  if (/^[._-]/u.test(canonical)) return 'bad_start';

  if (isReservedUsername(canonical)) return 'reserved';

  const scripts = new Set<string>();
  for (const char of chars) {
    if (NEUTRAL.test(char)) continue;

    const script = SCRIPT_TESTS.find(({ re }) => re.test(char));
    if (!script) return 'disallowed_character';
    scripts.add(script.name);
  }

  // All digits and punctuation, no letters at all.
  if (scripts.size === 0) return 'disallowed_character';
  if (scripts.size === 1) return undefined;

  const used = [...scripts];
  const ok = ALLOWED_COMBINATIONS.some((combo) => used.every((s) => combo.includes(s)));
  return ok ? undefined : 'mixed_script';
}

export function isValidUsername(input: string): boolean {
  return validateUsername(input) === undefined;
}

/**
 * Reserved-name check.
 *
 * Separator-insensitive, because `no-reply`, `no.reply` and `noreply` are the
 * same name to a reader and reserving only one spelling reserves nothing.
 */
export function isReservedUsername(candidate: string): boolean {
  const canonical = canonicalUsername(candidate.trim());
  if (RESERVED_PREFIXES.some((prefix) => canonical.startsWith(prefix))) return true;

  const stripped = canonical.replace(/[._-]/gu, '');
  for (const name of RESERVED_EXACT) {
    if (stripped === name.replace(/[._-]/gu, '')) return true;
  }
  return false;
}
